import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Youtube, Linkedin, Ear, Headphones, Heart } from "lucide-react";

const footerLinks = {
  solutions: [
    { name: "Cochlear Implants", href: "/cochlear-implant", icon: Ear },
    { name: "CI Products & Pricing", href: "/cochlear-implant/products" },
    { name: "Hearing Aids", href: "/hearing-aids", icon: Headphones },
  ],
  resources: [
    { name: "Educational Videos", href: "/videos" },
    { name: "All ENT Services", href: "/services" },
    { name: "Contact Us", href: "/contact" },
  ],
  support: [
    { name: "Book Appointment", href: "/book-appointment" },
    { name: "Support Deaf Children", href: "/csr", icon: Heart },
  ]
};

export default function Footer() {
  return (
    <footer className="bg-[#0A192F] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-width py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                <span className="text-[#0A192F] font-bold text-lg font-display">SS</span>
              </div>
              <div>
                <div className="font-display font-semibold text-lg">Dr. Sanjay Sachdeva</div>
                <div className="text-sm text-[#1E40AF]">ENT & Cochlear Implant</div>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-300">
              <a href="tel:+919910317286" className="flex items-start gap-3 hover:text-white transition-colors group">
                <Phone className="w-5 h-5 mt-0.5 text-[#1E40AF] group-hover:text-white" strokeWidth={1.5} />
                <div>
                  <div>9910317286</div>
                  <div>9810153116</div>
                </div>
              </a>
              <a href="mailto:entdrsachdeva25@gmail.com" className="flex items-center gap-3 hover:text-white transition-colors group">
                <Mail className="w-5 h-5 text-[#1E40AF] group-hover:text-white" strokeWidth={1.5} />
                <span className="text-sm">entdrsachdeva25@gmail.com</span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-[#1E40AF] flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm">B-163, Greater Kailash, Part I, New Delhi, 110048</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#1E40AF]" strokeWidth={1.5} />
                <span className="text-sm">Mon-Sat: 10:00 AM - 7:00 PM</span>
              </div>
            </div>
          </div>

          {/* Hearing Solutions */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-6 text-white">Hearing Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-slate-300 hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    {link.icon && <link.icon className="w-4 h-4 text-[#1E40AF] group-hover:text-white" strokeWidth={1.5} />}
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-6 text-white">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-slate-300 hover:text-white transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support & Social */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-slate-300 hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    {link.icon && <link.icon className="w-4 h-4 text-rose-400 group-hover:text-white" strokeWidth={1.5} />}
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Social Links */}
            <div className="mt-8">
              <h5 className="font-medium mb-4 text-white">Follow Us</h5>
              <div className="flex gap-3">
                <a 
                  href="https://www.youtube.com/@DrSachdevaent" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-red-600 transition-colors"
                  data-testid="youtube-link"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" strokeWidth={1.5} />
                </a>
                <a 
                  href="https://www.linkedin.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-width py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400">
          <p>&copy; {new Date().getFullYear()} Dr. Sanjay Sachdeva. All rights reserved.</p>
          <p className="text-center md:text-right">Principal Director - ENT & Cochlear Implant Surgery<br className="md:hidden" /> Max Super Speciality Hospital, Saket</p>
        </div>
      </div>
    </footer>
  );
}
